import { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Switch } from "../../../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";

type ConfirmKey = "delete-all" | `delete:${string}`;
type ModerationLogRow = {
	id: string;
	action: string | null;
	userId: string | null;
	roomId: string | null;
	userIp: string | null;
	messageContent: string;
	blockReason: string;
	createdAt: string | Date;
};

export function ModerationLogSection() {
	const [logs, setLogs] = useState<ModerationLogRow[]>([]);
	const [confirmKey, setConfirmKey] = useState<ConfirmKey | null>(null);
	const [isAIModerationEnabled, setIsAIModerationEnabled] = useState(false);
	const [aiBlockedMessageText, setAiBlockedMessageText] = useState("Message blocked by system");
	const [config, setConfig] = useState<{
		isPatternBlacklistEnabled: boolean;
		isIPBlacklistEnabled: boolean;
		isSplitTextDetectionEnabled: boolean;
		splitWindowMessageCount: number;
		splitWindowSeconds: number;
		splitMaxMessageLength: number;
	} | null>(null);
	const [saved, setSaved] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function load() {
		const { data } = await api.admin["moderation-logs"].all.get();

		if (data) setLogs(data as ModerationLogRow[]);
	}

	useEffect(() => {
		load();
		api.admin.config.get().then(({ data }) => {
			if (!data) return;
			setIsAIModerationEnabled(data.isAIModerationEnabled);
			setAiBlockedMessageText(data.aiBlockedMessageText);
			setConfig({
				isPatternBlacklistEnabled: data.isPatternBlacklistEnabled,
				isIPBlacklistEnabled: data.isIPBlacklistEnabled,
				isSplitTextDetectionEnabled: data.isSplitTextDetectionEnabled,
				splitWindowMessageCount: data.splitWindowMessageCount,
				splitWindowSeconds: data.splitWindowSeconds,
				splitMaxMessageLength: data.splitMaxMessageLength,
			});
		});
	}, []);

	async function handleDelete(id: string) {
		await api.admin["moderation-logs"].delete.delete({ id });
		setConfirmKey(null);
		load();
	}

	async function handleDeleteAll() {
		await api.admin["moderation-logs"]["delete-all"].delete();
		setConfirmKey(null);
		load();
	}

	async function handleConfigSave() {
		if (!config) return;

		const blockedText = aiBlockedMessageText.trim();
		if (!blockedText) {
			setError("blocked message text is required");
			return;
		}

		const { error } = await api.admin["update-blacklist-config"].patch({
			isAIModerationEnabled,
			aiBlockedMessageText: blockedText,
			...config,
		});

		if (error) {
			setError("failed to update AI moderation config");
			return;
		}

		setError(null);
		setSaved(true);
		setTimeout(() => setSaved(false), 2000);
	}

	return (
		<div className="space-y-4">
			<div className="grid max-w-2xl gap-4 border border-[#111] p-3 md:grid-cols-[1fr_auto]">
				<div className="flex items-center justify-between gap-4 md:col-span-2">
					<Label>AI Moderation</Label>
					<Switch checked={isAIModerationEnabled} onCheckedChange={setIsAIModerationEnabled} />
				</div>
				<div className="space-y-2">
					<Label>Blocked Message Text</Label>
					<Input
						value={aiBlockedMessageText}
						maxLength={200}
						onChange={(e) => setAiBlockedMessageText(e.target.value)}
					/>
				</div>
				<div className="flex items-end">
					<Button size="sm" onClick={handleConfigSave}>
						{saved ? "saved" : "apply"}
					</Button>
				</div>
				{error && <p className="text-xs text-red-500 font-mono md:col-span-2">// {error}</p>}
			</div>

			<div className="flex items-center justify-between gap-3">
				<p className="text-xs text-[#333] font-mono">latest {logs.length} moderation events</p>
				{confirmKey === "delete-all" ? (
					<div className="flex gap-1">
						<Button size="sm" variant="destructive" onClick={handleDeleteAll}>
							confirm delete all
						</Button>
						<Button size="sm" variant="ghost" onClick={() => setConfirmKey(null)}>
							cancel
						</Button>
					</div>
				) : (
					<Button size="sm" variant="ghost" disabled={logs.length === 0} onClick={() => setConfirmKey("delete-all")}>
						delete all
					</Button>
				)}
			</div>

			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Created</TableHead>
						<TableHead>Action</TableHead>
						<TableHead>User</TableHead>
						<TableHead>Room</TableHead>
						<TableHead>IP</TableHead>
						<TableHead>Message Content</TableHead>
						<TableHead>Block Reason</TableHead>
						<TableHead className="w-32">Actions</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{logs.map((log) => {
						const deleteKey: ConfirmKey = `delete:${log.id}`;

						return (
							<TableRow key={log.id}>
								<TableCell className="font-mono text-xs whitespace-nowrap">
									{new Date(log.createdAt).toLocaleString()}
								</TableCell>
								<TableCell className="font-mono text-xs uppercase">{log.action ?? "—"}</TableCell>
								<TableCell className="font-mono text-xs">{log.userId ?? "—"}</TableCell>
								<TableCell className="font-mono text-xs">{log.roomId ?? "—"}</TableCell>
								<TableCell className="font-mono text-xs">{log.userIp ?? "—"}</TableCell>
								<TableCell className="max-w-[360px] truncate text-[#00ffcc]">{log.messageContent}</TableCell>
								<TableCell className="font-mono text-xs">{log.blockReason}</TableCell>
								<TableCell>
									{confirmKey === deleteKey ? (
										<div className="flex gap-1 justify-end">
											<Button size="sm" variant="destructive" onClick={() => handleDelete(log.id)}>
												confirm
											</Button>
											<Button size="sm" variant="ghost" onClick={() => setConfirmKey(null)}>
												cancel
											</Button>
										</div>
									) : (
										<div className="flex justify-end">
											<Button size="sm" variant="ghost" onClick={() => setConfirmKey(deleteKey)}>
												delete
											</Button>
										</div>
									)}
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</div>
	);
}
